const POSITIONS_KEY = 'isa_positions';
const REVIEWS_KEY   = 'isa_reviews';

/* ── Supabase config ── */
const SUPABASE_URL     = process.env.REACT_APP_SUPABASE_URL     || 'https://cjriqjyldmwlmgddvihc.supabase.co';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNqcmlxanlsZG13bG1nZGR2aWhjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzgzODQ5MjAsImV4cCI6MjA5Mzk2MDkyMH0.i3JgYgamsD6bLW74afTO3Kz4tD0BTq-oxk4qPKWf0Qs';
const SYNC_ID          = 'portfolio';
const SYNC_TABLE       = 'positions';

/* ── Supabase helpers ── */
const sbHeaders = {
  'apikey':        SUPABASE_ANON_KEY,
  'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
  'Content-Type':  'application/json',
  'Prefer':        'resolution=merge-duplicates,return=representation',
};

/** Push positions array to Supabase (fire-and-forget, never throws) */
async function pushToCloud(positions) {
  try {
    await fetch(`${SUPABASE_URL}/rest/v1/${SYNC_TABLE}`, {
      method:  'POST',
      headers: sbHeaders,
      body:    JSON.stringify([{
        id:         SYNC_ID,
        data:       positions,
        updated_at: new Date().toISOString(),
      }]),
    });
  } catch (_) { /* offline — silently ignore */ }
}

/** Pull positions array from Supabase. Returns null if unavailable. */
export async function pullFromCloud() {
  try {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/${SYNC_TABLE}?id=eq.${SYNC_ID}&select=data,updated_at`,
      { headers: sbHeaders }
    );
    if (!res.ok) return null;
    const rows = await res.json();
    if (!rows?.length) return null;
    const raw = rows[0].data;
    const positions = Array.isArray(raw) ? raw
      : Array.isArray(raw?.positions) ? raw.positions
      : [];
    return { positions, updatedAt: rows[0].updated_at };
  } catch (_) {
    return null;
  }
}

/* ── Local storage ── */
export function loadPositions() {
  try {
    const raw = localStorage.getItem(POSITIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (Array.isArray(parsed?.positions)) return parsed.positions;
    return [];
  } catch {
    return [];
  }
}

export function savePositions(positions) {
  try {
    localStorage.setItem(POSITIONS_KEY, JSON.stringify(positions));
  } catch {}
  pushToCloud(positions); // async, non-blocking
}

export function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

export function calcCushion(currentPrice, stopPrice) {
  if (!currentPrice || currentPrice <= 0) return 0;
  return ((currentPrice - stopPrice) / currentPrice) * 100;
}

export function getStatus(cushion) {
  if (cushion > 15) return 'GREEN';
  if (cushion >= 5) return 'AMBER';
  return 'RED';
}

/* ── Weekly Reviews ── */
export function loadReviews() {
  try { return JSON.parse(localStorage.getItem(REVIEWS_KEY)) ?? []; }
  catch { return []; }
}
export function saveReviews(reviews) {
  try { localStorage.setItem(REVIEWS_KEY, JSON.stringify(reviews)); } catch {}
}

/* ── Export / Import ── */
const LS_KEYS = ['isa_positions', 'isa_dca_inputs', 'isa_fm_stocks', 'isa_fm_themes', 'isa_reviews'];// eslint-disable-line no-unused-vars

export function exportAllData() {
  const read = k => { try { return JSON.parse(localStorage.getItem(k)); } catch { return null; } };
  return {
    version:    '1.0',
    exportedAt: new Date().toISOString(),
    positions:  read('isa_positions')  ?? [],
    dcaInputs:  read('isa_dca_inputs') ?? {},
    fmStocks:   read('isa_fm_stocks')  ?? [],
    fmThemes:   read('isa_fm_themes')  ?? null,
    reviews:    read('isa_reviews')    ?? [],
  };
}

export function importAllData(data) {
  if (!data || !data.version) throw new Error('Invalid backup file — missing version field.');
  const write = (k, v) => { if (v != null) try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };
  write('isa_positions',  data.positions);
  write('isa_dca_inputs', data.dcaInputs);
  write('isa_fm_stocks',  data.fmStocks);
  write('isa_fm_themes',  data.fmThemes);
  write('isa_reviews',    data.reviews);
  // Also push to cloud when importing
  if (data.positions) pushToCloud(data.positions);
}
